import { useContext } from 'react'
import useSWR from 'swr'
import groupBy from 'lodash.groupby'
import {
  Disturbance,
  getMultipleDisturbancesForStops,
} from '../data/travelinfo-disturbance.api'
import { ServerTimeContext } from '../contexts/serverTime.context'
import {
  IRTIS3_REFRESH_INTERVAL,
  CONTENTFUL_IRTIS3_NOTIFICATION_ASSET_ID,
  CONTENTFUL_IRTIS3_NOTIFICATION_REFRESH_INTERVAL,
} from '../constants'
import { getContentfulAsset } from '../data/contentful.api'
import { mapContentfulMessageAndDisturbancesToMessages } from '../utils/message.utils'

export function groupDisturbancesByDescription(
  disturbances: Disturbance[],
): Disturbance[] {
  const grouped = groupBy(disturbances, 'description')
  return Object.entries(grouped).map(([key, items]) => ({
    description: key,
    impactedLines: items.flatMap((disturbance) => disturbance.impactedLines),
    disruptedStops: items.flatMap((disturbance) => disturbance.disruptedStops),
  }))
}

export function useMessagesForStops({
  haltenummers,
  isPreview = false,
}: {
  haltenummers: string
  isPreview?: boolean
}) {
  const stopIds = haltenummers.split('+')
  const { getCalibratedDateTime } = useContext(ServerTimeContext)

  const {
    data: disturbances,
    error: disturbanceError,
    isLoading: loadingDisturbances,
  } = useSWR(
    `disturbances-stops-${haltenummers}`,
    () =>
      getMultipleDisturbancesForStops({
        startDateTime: getCalibratedDateTime(),
        stopIds,
      }),
    {
      refreshInterval: IRTIS3_REFRESH_INTERVAL,
      fallbackData: [],
    },
  )

  const {
    data: contentfulMessage,
    error: contentfulError,
    isLoading: loadingContentfulMessage,
  } = useSWR(
    `message-contentful`,
    () =>
      getContentfulAsset(CONTENTFUL_IRTIS3_NOTIFICATION_ASSET_ID, isPreview),
    {
      refreshInterval: CONTENTFUL_IRTIS3_NOTIFICATION_REFRESH_INTERVAL,
      fallbackData: undefined,
      errorRetryInterval: CONTENTFUL_IRTIS3_NOTIFICATION_REFRESH_INTERVAL,
    },
  )

  const limitDisturbances = 3 // Show maximum 3 messages. RISEWEB-4128
  return {
    messages: mapContentfulMessageAndDisturbancesToMessages(
      disturbances,
      contentfulMessage,
    ).slice(0, limitDisturbances),
    error: disturbanceError || contentfulError,
    isLoading: loadingDisturbances || loadingContentfulMessage,
  }
}
